import React, { useRef, useState } from 'react'
import { useDispatch } from "react-redux";
import { useHistory, NavLink } from 'react-router-dom';

import hoyaSourceLogo from '../../assets/images/hoyaSourceLogo.svg';
import useInput from '../../hooks/use-input';
import Input from '../UI/Input'
import Button from '../UI/Button';
import { login } from '../../lib/api';
import { loginFailure, loginStart, loginSuccess, updateUserImage, updateUserName } from '../../redux/userSlice'
import { useModal } from '../../store/ModalContext';
import { isPassword, isEmail } from '../UI/Validations';
import { clearAppList, clearSubscribeAppList } from '../../redux/appSlice';
import { GoogleReCaptchaProvider, GoogleReCaptcha } from "react-google-recaptcha-v3";

const Login = () => {

    const dispatch = useDispatch();
    const history = useHistory();

    const emailInputRef = useRef();
    const passwordInputRef = useRef();
    const [isLoading, setIsLoading] = useState(false);
    const [isLoginFailed, setIsLoginFailed] = useState(false);
    const [errorMessage, setIsErrorMessage] = useState("");
    const [type, setType] = useState('password')

    const { openForgotPasswordModal } = useModal();

    const {
        value: emailValue,
        isValid: emailIsValid,
        hasError: emailHasError,
        valueChangeHandler: emailChangeHandler,
        inputBlurHandler: emailBlurHandler,
    } = useInput(isEmail);

    const {
        value: passwordValue,
        isValid: passwordIsValid,
        hasError: passwordHasError,
        valueChangeHandler: passwordChangeHandler,
        inputBlurHandler: passwordBlurHandler,
    } = useInput(isPassword);

    let formIsValid = false;

    if (!passwordIsValid && !emailIsValid) {
        formIsValid = true;
    }


    const toggleShowPassword = () => {
        if (type === 'password') {
            setType('text')
        } else {
            setType('password')
        }
    }

    const forgotPassword = () => {
        openForgotPasswordModal();
    }

    const goToRegister = () => {
        history.push('/register')
    }

    const goBack = () => {
        history.go(-1);        
    }

    const submitHandler = (event) => {
        event.preventDefault();
        dispatch(loginStart());

        const enteredEmail = emailInputRef.current.value;
        const enteredPassword = passwordInputRef.current.value;

        setIsLoading(true);

        const credential = ({
            name: enteredEmail,
            pass: enteredPassword
        })

        login(credential)
            .then((res) => {
                setIsLoading(false);
                if (res.ok) {
                    return res.json();
                } else {
                    return res.json().then((data) => {
                        let errMessage = 'Invalid email or password.';
                        setIsLoginFailed(true);
                        setIsErrorMessage(errMessage);
                        if (data && data.error && data.error.message) {
                            errMessage = data.error.message;
                        }
                        throw new Error(errMessage);
                    });
                }
            })
            .then((data) => {
                dispatch(loginSuccess(data));
                dispatch(updateUserName(data.user_extra_details?.first_name + " " + data.user_extra_details?.last_name))
                dispatch(updateUserImage(data.user_extra_details?.user_picture))
                dispatch(clearAppList())
                dispatch(clearSubscribeAppList())
                history.push('/');
            })
            .catch((err) => {
                setIsLoading(false);
                dispatch(loginFailure());
                dispatch(clearAppList())
                dispatch(clearSubscribeAppList())
            });
    };

    const passwordClasses = passwordHasError ? 'passwordField p-[10px] rounded border border-loginFieldBorder h-10 flex items-center justify-between gap-5 bg-white invalid-password' : 'passwordField p-[10px] rounded border border-loginFieldBorder h-10 flex items-center justify-between gap-5 bg-white';
    const emailClasses = emailHasError ? 'emailSection gap-1 flex flex-col invalid' : 'emailSection gap-1 flex flex-col';
    const passwordInputClass = passwordHasError ? '!text-sm text-[#666] w-full block outline-none shadow-none invalid-pass-bg placeholder:text-[#666]' : '!text-sm text-[#666] w-full block outline-none shadow-none  placeholder:text-[#666]'


    return (
        <div className="grow loginHolder !h-auto w-full flex items-center gap-10 justify-evenly bg-[#F4F8FD] sm:bg-[#F4F8FD] lg:bg-white landscape:h-auto  md:landscape:h-full landscape:overflow-auto sm:py-14 py-10">
            <div className="loginSection basis-[80%] max-[500px]:basis-[100%] sm:basis-[450px] md:basis-[370px] shrink-0 md:shrink-0 px-10 md:px-0">
                <div className="login w-full">
                    <NavLink to="/">
                        <img src={hoyaSourceLogo} alt="logo" className="w-[200px] sm:w-full mb-6 sm:mb-12 mx-auto object-cover lg:block sm:mx-auto" />
                    </NavLink>
                    <h2 className="loginTitle text-[#354665] barlowBold text-xl">LOGIN</h2>
                    <form className="loginForm mt-6" onSubmit={submitHandler}>
                        <div className="space-y-4 sm:space-y-6">
                            <div className={emailClasses}>
                                <Input
                                    ref={emailInputRef}
                                    label="Email"
                                    labelClass="emailLabel text-[#333] text-sm font-normal leading-5"
                                    input={{
                                        id: "email",
                                        type: "email",
                                        value: emailValue,
                                        placeholder: "Enter Email",
                                        onChange: emailChangeHandler,
                                        onBlur: emailBlurHandler,
                                        className:
                                            "!text-sm p-[10px] rounded border border-loginFieldBorder text-[#666] h-10 bg-white loginField placeholder:text-[#666]",
                                    }}
                                />
                                {emailHasError && (
                                    <p className="error-text">
                                        {emailIsValid}
                                    </p>
                                )}
                            </div>
                            <div className="passwordSection gap-[6px] flex flex-col relative">
                                <div className="passwordLabel flex justify-between">
                                    <span className="text-[#333] text-sm font-normal leading-5">Password</span>
                                    <button className="text-primaryBlue leading-5 font-normal text-sm cursor-pointer" type="button" onClick={forgotPassword}>Forgot password?</button>
                                </div>
                                <div className={passwordClasses}>
                                    <input
                                        ref={passwordInputRef}
                                        type={type}
                                        id="password"
                                        name="password"
                                        onChange={passwordChangeHandler}
                                        onBlur={passwordBlurHandler}
                                        value={passwordValue}
                                        className={passwordInputClass}
                                        placeholder="Enter Password"
                                    />

                                    <div
                                        className="eyeIcon cursor-pointer opacity-50"
                                        onClick={toggleShowPassword}
                                    >
                                        {type === "password" ? (
                                            <svg
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <g clipPath="url(#clip0_3379_10992)">
                                                    <path
                                                        d="M14.5299 9.46992L9.46992 14.5299C8.81992 13.8799 8.41992 12.9899 8.41992 11.9999C8.41992 10.0199 10.0199 8.41992 11.9999 8.41992C12.9899 8.41992 13.8799 8.81992 14.5299 9.46992Z"
                                                        stroke="#292D32"
                                                        strokeWidth="1.5"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                    <path
                                                        d="M17.8198 5.76998C16.0698 4.44998 14.0698 3.72998 11.9998 3.72998C8.46984 3.72998 5.17984 5.80998 2.88984 9.40998C1.98984 10.82 1.98984 13.19 2.88984 14.6C3.67984 15.84 4.59984 16.91 5.59984 17.77"
                                                        stroke="#292D32"
                                                        strokeWidth="1.5"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                    <path
                                                        d="M8.41992 19.5299C9.55992 20.0099 10.7699 20.2699 11.9999 20.2699C15.5299 20.2699 18.8199 18.1899 21.1099 14.5899C22.0099 13.1799 22.0099 10.8099 21.1099 9.39993C20.7799 8.87993 20.4199 8.38993 20.0499 7.92993"
                                                        stroke="#292D32"
                                                        strokeWidth="1.5"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                    <path
                                                        d="M15.5104 12.7C15.2504 14.11 14.1004 15.26 12.6904 15.52"
                                                        stroke="#292D32"
                                                        strokeWidth="1.5"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                    <path
                                                        d="M9.47 14.53L2 22"
                                                        stroke="#292D32"
                                                        strokeWidth="1.5"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                    <path
                                                        d="M22.0003 2L14.5303 9.47"
                                                        stroke="#292D32"
                                                        strokeWidth="1.5"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </g>
                                                <defs>
                                                    <clipPath id="clip0_3379_10992">
                                                        <rect width="24" height="24" fill="white" />
                                                    </clipPath>
                                                </defs>
                                            </svg>
                                        ) : (
                                            <svg
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <g clipPath="url(#clip0_3379_10991)">
                                                    <path
                                                        d="M15.5799 11.9999C15.5799 13.9799 13.9799 15.5799 11.9999 15.5799C10.0199 15.5799 8.41992 13.9799 8.41992 11.9999C8.41992 10.0199 10.0199 8.41992 11.9999 8.41992C13.9799 8.41992 15.5799 10.0199 15.5799 11.9999Z"
                                                        stroke="#292D32"
                                                        strokeWidth="1.5"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                    <path
                                                        d="M11.9998 20.27C15.5298 20.27 18.8198 18.19 21.1098 14.59C22.0098 13.18 22.0098 10.81 21.1098 9.39997C18.8198 5.79997 15.5298 3.71997 11.9998 3.71997C8.46984 3.71997 5.17984 5.79997 2.88984 9.39997C1.98984 10.81 1.98984 13.18 2.88984 14.59C5.17984 18.19 8.46984 20.27 11.9998 20.27Z"
                                                        stroke="#292D32"
                                                        strokeWidth="1.5"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </g>
                                                <defs>
                                                    <clipPath id="clip0_3379_10991">
                                                        <rect width="24" height="24" fill="white" />
                                                    </clipPath>
                                                </defs>
                                            </svg>
                                        )}
                                    </div>
                                </div>
                                {passwordHasError && (
                                    <p className="error-text">{passwordIsValid}</p>
                                )}
                            </div>
                            {!passwordHasError && !emailHasError && isLoginFailed && (
                                <div className="error-message">
                                    <span className="error-text">{errorMessage}</span>
                                </div>
                            )}
                            {/* //sitekey */}
                            <GoogleReCaptchaProvider reCaptchaKey="6LfltCAqAAAAAPibZsMhBQ3X4s0UE8Fpzjf6QpTI">
                                <GoogleReCaptcha
                                className="google-recaptcha-custom-class"
                                />
                            </GoogleReCaptchaProvider>
                            {!isLoading && (<Button disabled={!formIsValid} type="submit" className={!formIsValid ? "btn disabled loginBtn w-full bg-primaryBlue h-[46px] font-bold leading-5 text-base  justify-center items-center rounded text-white text-center flex !mt-6 md:!mt-10" : "btn loginBtn w-full bg-primaryBlue h-[46px] font-bold leading-5 text-base  justify-center items-center rounded text-white text-center flex !mt-6 md:!mt-10"}>Login</Button>)}
                            <div className="mt-6 sm:mt-8">
                                <p className="text-center text-[#354665] font-normal text-sm  leading-5 sm:text-base sm:leading-6">Don’t have an account yet?<br />Click here to <span className="font-bold underline text-primaryText cursor-pointer"><button type='button' onClick={goToRegister}>register.</button></span></p>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
            <div className="videoSection !hidden lg:flex md:basis-[420px] shrink">
                <div className="videoHolder aspect-square w-full">
                    <video src="./videos/Login.mp4" className="h-full" autoPlay muted></video>
                </div>
            </div>
            <div className="closeIconHolder absolute left-10 sm:right-14 sm:left-auto top-6 cursor-pointer sm:landscape:right-14 xl:hidden lg:block" onClick={goBack}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M4.7475 20.1575C4.5575 20.1575 4.3675 20.0875 4.2175 19.9375C3.9275 19.6475 3.9275 19.1675 4.2175 18.8775L17.8775 4.2175C18.1675 3.9275 18.6475 3.9275 18.9375 4.2175C19.2275 4.5075 19.2275 4.9875 18.9375 5.2775L5.2775 19.9375C5.1375 20.0875 4.9375 20.1575 4.7475 20.1575Z" fill="#717375" stroke="#717375"></path><path d="M18.4075 20.1575C18.2175 20.1575 18.0275 20.0875 17.8775 19.9375L4.2175 5.2775C3.9275 4.9875 3.9275 4.5075 4.2175 4.2175C4.5075 3.9275 4.9875 3.9275 5.2775 4.2175L18.9375 18.8775C19.2275 19.1675 19.2275 19.6475 18.9375 19.9375C18.7875 20.0875 18.5975 20.1575 18.4075 20.1575Z" fill="#717375" stroke="#717375"></path>
                </svg>
            </div>
        </div >
    );
};
export default Login;
